import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Button, Dialog, DialogContent, DialogTitle, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import BackdropLoader from "@entrilia/shared/components/fullScreenLoader/BackdropLoader";
import ChipsMultilineInput from "@entrilia/shared/components/inputs/ChipsMultilineInput";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import CompanySelectionDialogIcon from "@entrilia/shared/icons/CompanySelectionDialogIcon";
import MembersContext from "../MembersContext";
import AccessSection from "./AccessSection";
import CompaniesManageSection from "./CompaniesManageSection";
import DeveloperPortal from "./DeveloperPortal";
import InviteMembersContext from "./InviteMembersContext";
import ManagedMemberDetails from "./ManagedMemberDetails";
import OrganizationRoleSelector from "./OrganizationRoleSelector";

const InviteMembersContainer = () => {
  const theme = useTheme();
  const borderLineStyle = `1px ${theme.palette.divider} solid`;

  const { isSending, showInviteMembersDialog, members, companiesList } = useDefinedContext(MembersContext);

  const {
    emails,
    updateEmails,
    selectedCompanyAccess,
    isValidConfiguration,
    handleClose,
    sendInvites,
    getDialogTitle,
    managedMember,
    applyMemberChanges,
    isCurrentUser,
  } = useDefinedContext(InviteMembersContext);

  const devPortalAccessibleCompanies = useMemo(() => {
    const accessibleCompanies = members.find((m) => m.userId === managedMember?.userId)?.accessToDeveloperPortal || [];
    return accessibleCompanies.map((companyCode) => {
      const companyData = companiesList.find((c) => c.value === companyCode);
      return { title: companyData?.label || "", icon: companyData?.iconSrc, code: companyData?.value || "" };
    });
  }, [members, managedMember, companiesList]);

  return (
    <Dialog
      open={showInviteMembersDialog}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      onClose={handleClose}
      sx={{ "& .MuiDialog-paper": { backgroundColor: "background.grey", height: "88vh", top: 12 } }}
    >
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: "100%" }}>
          <Grid
            size={{ xs: 7, md: 8, lg: 9, xl: 9.3 }}
            sx={{
              alignItems: "start",
              borderRight: borderLineStyle,
              p: 3,
              height: "100%",
              maxHeight: "100%",
              overflowY: "auto",
            }}
          >
            <Stack sx={{ gap: 1.5 }}>
              <Typography variant="subtitle1">{managedMember ? "User" : "Send invite to users"}</Typography>
              {!managedMember && (
                <ChipsMultilineInput
                  values={emails}
                  placeholder="Enter one or multiple emails, comma or space separated"
                  onChange={updateEmails}
                  inputValidator={emailValidator}
                  sanitizeValue={sanitizeEmail}
                  freeSolo
                  removeDuplicatesOnPaste={false}
                  restrictedItems={members.map((member) => member.email)}
                  restrictedItemsHelperText="User with such email is already registered"
                />
              )}
              {managedMember && (
                <ManagedMemberDetails
                  userName={managedMember.name}
                  email={managedMember.email}
                  isCurrentUser={isCurrentUser(managedMember.userId)}
                />
              )}
              <Typography sx={{ mt: 1 }} variant="subtitle1">
                Organization Role
              </Typography>
              <OrganizationRoleSelector />
              {devPortalAccessibleCompanies.length > 0 && (
                <>
                  <Typography sx={{ mt: 2.5, mb: 0.5 }} variant="subtitle2">
                    Developer portal
                  </Typography>
                  <DeveloperPortal accessibleCompanies={devPortalAccessibleCompanies} />
                </>
              )}
              <CompaniesManageSection />
            </Stack>
          </Grid>
          <Grid
            size="grow"
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              maxHeight: "100%",
              overflowY: "auto",
            }}
          >
            {!selectedCompanyAccess && (
              <Stack spacing={1} sx={{ alignItems: "center", justifyContent: "center" }}>
                <CompanySelectionDialogIcon sx={{ width: 80, height: 80 }} />
                <Typography color="textSecondary">Select company to configure access</Typography>
              </Stack>
            )}
            {selectedCompanyAccess && <AccessSection companyAccess={selectedCompanyAccess} />}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={handleClose}>
        {!managedMember && (
          <Button
            variant="contained"
            color="primary"
            disabled={!isValidConfiguration}
            loading={isSending}
            onClick={sendInvites}
            endIcon={<ArrowForwardRoundedIcon />}
          >
            Send Invite
          </Button>
        )}
        {managedMember && (
          <Button
            variant="contained"
            color="primary"
            disabled={!isValidConfiguration}
            loading={isSending}
            onClick={applyMemberChanges}
          >
            Apply
          </Button>
        )}
      </DialogButtons>
      <BackdropLoader open={isSending} />
    </Dialog>
  );
};

export default InviteMembersContainer;
